import type { CheckboxProps } from '@mui/material/Checkbox';
import type { ChipProps } from '@mui/material/Chip';
import type { FormControlProps } from '@mui/material/FormControl';
import type { FormHelperTextProps } from '@mui/material/FormHelperText';
import type { InputLabelProps } from '@mui/material/InputLabel';
import type { SelectProps } from '@mui/material/Select';
import type { TextFieldProps } from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';

import SolarShuffleOutline from '@/components-v2/icons/SolarShuffleOutline';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import { Controller, useFormContext } from 'react-hook-form';

// ----------------------------------------------------------------------

type RHFSelectProps<T> = TextFieldProps & {
  name: string;
  native?: boolean;
  children: React.ReactNode;
  slotProps?: {
    paper?: SxProps<Theme>;
  };
  parser?: (value: T) => string | undefined;
  formatter?: (value: string) => T;
};

export function RHFSelect<T>({
  name,
  native,
  children,
  slotProps,
  helperText,
  inputProps,
  InputLabelProps,
  parser,
  formatter,
  ...other
}: RHFSelectProps<T>) {
  const { control } = useFormContext();

  const labelId = `${name}-select-label`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={parser ? parser(field.value) : field.value}
          onChange={(val) => field.onChange(formatter ? formatter(val.target.value) : val.target.value)}
          select
          fullWidth
          SelectProps={{
            native,
            MenuProps: { PaperProps: { sx: { maxHeight: 220, ...slotProps?.paper } } },
            sx: { textTransform: 'capitalize' },
          }}
          InputLabelProps={{ htmlFor: labelId, ...InputLabelProps }}
          inputProps={{ id: labelId, ...inputProps }}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
        >
          {children}
        </TextField>
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFMultiSelectProps<T> = FormControlProps & {
  name: string;
  label?: string;
  chip?: boolean;
  checkbox?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode;
  options: {
    label: string;
    value: string;
  }[];
  slotProps?: {
    chip?: ChipProps;
    select: SelectProps;
    checkbox?: CheckboxProps;
    inputLabel?: InputLabelProps;
    formHelperText?: FormHelperTextProps;
  };
  parser?: (value: T) => string[];
  formatter?: (value: string[]) => T;
};

export function RHFMultiSelect<T>({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  slotProps,
  helperText,
  parser,
  formatter,
  ...other
}: RHFMultiSelectProps<T>) {
  const { control } = useFormContext();

  const labelId = `${name}-select-label`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selectedValues = parser ? parser(field.value) : field.value;
        return (
          <FormControl error={!!error} {...other}>
            {label && (
              <InputLabel htmlFor={labelId} {...slotProps?.inputLabel}>
                {label}
              </InputLabel>
            )}

            <Select
              {...field}
              value={selectedValues}
              onChange={(e) => {
                field.onChange(formatter ? formatter(e.target.value as string[]) : (e.target.value as string[]));
              }}
              multiple
              displayEmpty={!!placeholder}
              label={label}
              renderValue={(selected) => {
                const selectedItems = options.filter((item) => (selected as string[]).includes(item.value));

                if (!selectedItems.length && placeholder) {
                  return <Box sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
                }

                if (chip) {
                  return (
                    <Box sx={{ gap: 0.5, display: 'flex', flexWrap: 'wrap' }}>
                      {selectedItems.map((item) => (
                        <Chip key={item.value} size="small" variant="soft" label={item.label} {...slotProps?.chip} />
                      ))}
                    </Box>
                  );
                }

                return selectedItems.map((item) => item.label).join(', ');
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '12px 16px',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      padding: '4px 8px',
                      minHeight: '36px',
                    },
                  },
                },
              }}
              {...slotProps?.select}
              inputProps={{ id: labelId, ...slotProps?.select?.inputProps }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {checkbox && (
                    <Checkbox
                      size="small"
                      disableRipple
                      checked={selectedValues.includes(option.value)}
                      {...slotProps?.checkbox}
                    />
                  )}

                  {option.label}
                </MenuItem>
              ))}
            </Select>

            {(!!error || helperText) && (
              <FormHelperText error={!!error} {...slotProps?.formHelperText}>
                {error ? error?.message : helperText}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

type RHFSelectWithRandomProps<T> = RHFSelectProps<T> & {
  randomSelectOptions?: Array<{ value: string; label: string }>;
};

export function RHFSelectWithRandom<T>({
  name,
  native,
  children,
  slotProps,
  helperText,
  inputProps,
  InputLabelProps,
  parser,
  formatter,
  randomSelectOptions,
  ...other
}: RHFSelectWithRandomProps<T>) {
  const { control } = useFormContext();
  const theme = useTheme();

  const labelId = `${name}-select-label`;

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={parser ? parser(field.value) : field.value}
            onChange={(val) => field.onChange(formatter ? formatter(val.target.value) : val.target.value)}
            select
            fullWidth
            SelectProps={{
              native,
              MenuProps: { PaperProps: { sx: { maxHeight: 220, ...slotProps?.paper } } },
              sx: { textTransform: 'capitalize' },
            }}
            InputLabelProps={{ htmlFor: labelId, ...InputLabelProps }}
            inputProps={{ id: labelId, ...inputProps }}
            error={!!error}
            helperText={error ? error?.message : helperText}
            sx={{ flex: 1 }}
            {...other}
          >
            {children}
          </TextField>
        )}
      />

      {randomSelectOptions && (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Tooltip title="Random Select">
              <IconButton
                onClick={() => {
                  if (randomSelectOptions.length) {
                    const currentValue = parser ? parser(field.value) : field.value;
                    const availableOptions = randomSelectOptions.filter((option) => option.value !== currentValue);

                    if (availableOptions.length > 0) {
                      const randomOption = availableOptions[Math.floor(Math.random() * availableOptions.length)];
                      field.onChange(formatter ? formatter(randomOption.value) : randomOption.value);
                    } else if (randomSelectOptions.length > 1) {
                      // 如果所有选项都被过滤掉了，但总选项数大于1，说明当前值可能不在选项中，直接随机选择
                      const randomOption = randomSelectOptions[Math.floor(Math.random() * randomSelectOptions.length)];
                      field.onChange(formatter ? formatter(randomOption.value) : randomOption.value);
                    }
                  }
                }}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  border: '1px solid',
                  borderColor: alpha(theme.palette.grey[500], 0.2),
                  borderRadius: 1,
                  color: theme.palette.text.secondary,
                  backgroundColor: 'transparent',
                  transition: theme.transitions.create(['border-color', 'color'], {
                    duration: theme.transitions.duration.shortest,
                  }),
                  '@media (hover: hover)': {
                    '&:hover': {
                      borderColor: theme.palette.text.primary,
                      color: theme.palette.text.primary,
                    },
                  },
                }}
              >
                <SolarShuffleOutline height={20} width={20} />
              </IconButton>
            </Tooltip>
          )}
        />
      )}
    </Box>
  );
}
